import React, { useEffect } from "react";
import {
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaFileMedical,
  FaDatabase,
  FaEye,
  FaCookieBite,
  FaSyncAlt,
  FaEnvelope,
  FaCheckCircle
} from "react-icons/fa";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const policies = [
    {
      icon: <FaShieldAlt size={20} />,
      title: "1. Data Collection",
      desc: "We collect only necessary personal identifiers (Name, Mobile, Email, Age, Gender) and test booking details required to schedule home collections and generate clinical reports.",
    },
    {
      icon: <FaFileMedical size={20} />,
      title: "2. Health & Diagnostic Records",
      desc: "Medical records and test report PDFs are securely archived on HIPAA-compliant encrypted storage and shared only with the verified patient and diagnosing lab.",
    },
    {
      icon: <FaLock size={20} />,
      title: "3. End-to-End Encryption",
      desc: "All communication between your browser/app and our servers is secured via 256-bit SSL encryption. We never sell or monetize your personal health data.",
    },
    {
      icon: <FaDatabase size={20} />,
      title: "4. DPDP Act 2023 Compliance",
      desc: "Our data protection practices strictly adhere to the Digital Personal Data Protection (DPDP) Act, 2023, ensuring transparency, consent, and user data rights.",
    },
    {
      icon: <FaEye size={20} />,
      title: "5. Third-Party Lab Sharing",
      desc: "Your sample collection details are shared strictly with the selected NABL/ISO accredited partner lab solely for processing and diagnostic sample testing.",
    },
    {
      icon: <FaCookieBite size={20} />,
      title: "6. Cookies & Session Storage",
      desc: "We use essential session tokens and cookies to maintain your login state, location preference, and shopping cart persistence across page reloads.",
    },
    {
      icon: <FaUserShield size={20} />,
      title: "7. User Data Rights",
      desc: "You retain full rights to request an export of your health history, rectify inaccuracies, or request permanent deletion of your account and records anytime.",
    },
    {
      icon: <FaSyncAlt size={20} />,
      title: "8. Policy Revisions",
      desc: "We periodically review and update this privacy policy to reflect evolving data regulations and enhanced security features on MedrphaLab.",
    },
  ];

  return (
    <div className="w-full min-h-screen bg-slate-50 text-slate-800">
      
      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white py-16 md:py-20 px-4 sm:px-6 lg:px-12 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white border border-white/20 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <FaLock className="text-amber-300" />
            <span>Privacy & Security</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-xs sm:text-sm text-red-100 max-w-2xl mx-auto">
            ARMC PHARMA PRIVATE LIMITED • Committed to protecting your medical data
          </p>
        </div>
      </section>

      {/* POLICY GRID */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="grid sm:grid-cols-2 gap-6">
          {policies.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs hover:shadow-lg hover:border-red-200 transition-all duration-300 group"
            >
              <div className="flex items-center gap-3.5 mb-3">
                <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-all shadow-xs shrink-0">
                  {item.icon}
                </div>
                <h3 className="text-base font-bold text-slate-900">
                  {item.title}
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed pl-1">
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* GRIEVANCE OFFICER SECTION */}
        <div className="mt-12 bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <FaCheckCircle className="text-emerald-500 text-xl" />
            <h4 className="text-base font-bold text-slate-900">
              Data Protection & Grievance Officer
            </h4>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-4">
            In accordance with IT Act 2000 and DPDP Act 2023, if you have any questions or complaints regarding data processing, please contact:
          </p>
          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 text-xs sm:text-sm space-y-1.5 text-slate-700">
            <p><strong>Entity:</strong> ARMC PHARMA PRIVATE LIMITED</p>
            <p><strong>Address:</strong> D-43 1st Floor, Sector 6, Noida, Uttar Pradesh - 201301</p>
            <p><strong>Email:</strong> <a href="mailto:info@medrpha.com" className="text-red-600 underline font-semibold">info@medrpha.com</a></p>
          </div>
        </div>
      </section>

    </div>
  );
}
